import React, { useEffect, useState } from 'react';
import { resumeService } from '../services/api';
import type { Resume } from '../types';
import { Eye, X } from 'lucide-react';
import { augmentPreviewHtml } from '../utils/previewHtml';

interface Props {
  resume: Resume;
  onClose: () => void;
  className?: string;
}

const ResumePreviewModal: React.FC<Props> = ({ resume, onClose, className = 'z-100' }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    resumeService
      .previewSavedResume(resume as unknown as Resume)
      .then((response) => {
        if (!cancelled) setHtml(augmentPreviewHtml(response.data, { full: true }));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [resume]);

  return (
    <div className={`fixed inset-0 ${className} flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm`}>
      <div className="bg-white rounded-card shadow-elevated w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 flex-shrink-0">
          <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
            <Eye className="w-4 h-4 text-brand-500" />
            Preview — {resume.personalInfo.fullName}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-foreground rounded-btn hover:bg-zinc-50 transition-colors"
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-zinc-100 p-4">
          {error ? (
            <div className="text-sm text-zinc-400 py-10 text-center">Failed to render preview.</div>
          ) : !html ? (
            <div className="flex items-center justify-center h-64 text-sm text-zinc-400">Loading preview...</div>
          ) : (
            <iframe
              title="Resume preview"
              srcDoc={html}
              sandbox="allow-same-origin"
              className="w-full h-[70vh] rounded-card border border-zinc-200 bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumePreviewModal;
